import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  alpha,
  Slider,
  styled,
  Card,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  BACKGROUND_GRADIENT,
  BACKGROUND_GRADIENT_TRANSPARENT,
  BUTTON_BG_COLOR,
} from './AutoSpotlightDialog';
import { parseCurrencyString } from '../../utils/formatCarPrice';
import { typographyClamps } from '../../utils/utils';
import { IsNotNullOrEmpty } from '../../utils/validation';

const DownPaymentSlider = styled(Slider)({
  color: '#5B9FD8',
  height: 8,
  padding: '15px 0',
  '& .MuiSlider-track': {
    border: 'none',
    borderRadius: 4,
  },
  '& .MuiSlider-rail': {
    backgroundColor: '#D5E5F3',
    opacity: 1,
    borderRadius: 4,
  },
  '& .MuiSlider-thumb': {
    height: 28,
    width: 28,
    backgroundColor: '#fff',
    border: '3px solid #5B9FD8',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: '0 2px 8px rgba(91, 159, 216, 0.3)',
    },
    '&::before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    display: 'none',
  },
});

interface PaymentInformationModelProps {
  carData: any;
  onSubmit: (downPayment: number) => void;
  onBack: () => void;
  gotoDealershipWebsite: () => void;
}

const PaymentInformationModel: React.FC<PaymentInformationModelProps> = ({
  carData,
  onBack,
  onSubmit,
  gotoDealershipWebsite,
}) => {
  const [value, setValue] = useState<number>(2000);

  const parsedPrice = parseCurrencyString(carData?.price);

  const handleChange = (event: Event, newValue: number | number[]) => {
    setValue(newValue as number);
  };

  const formatCurrency = (val: number): string => {
    if (val >= 1000) {
      return `$${(val / 1000).toFixed(0)}K`;
    }
    return `$${val}`;
  };

  const calculateEMI = (price: number, apr = 7.5, months = 72) => {
    const principal = price - value;
    const monthlyRate = apr / 100 / 12;

    if (monthlyRate === 0) {
      return principal / months;
    }

    const factor = Math.pow(1 + monthlyRate, months);
    const emi = (principal * monthlyRate * factor) / (factor - 1);

    return Number(emi.toFixed(2));
  };

  const formatEMIAmount = (amount: number) => {
    const parsedAmount = parseCurrencyString(amount.toString());
    return parsedAmount?.amount !== '-'
      ? `${parsedAmount?.symbolOrCode}${parsedAmount?.amount}`
      : '-';
  };

  const getDownPaymentView = ({ min = 0, max = 10000, step = 100 }) => {
    return (
      <Box sx={{ width: '100%' }}>
        {/* Header with label and value */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            // mb: 1,
          }}
        >
          <Typography
            variant='body2'
            sx={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 400,
            }}
          >
            Down Payment
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: '#333',
              fontSize: '16px',
              fontWeight: 500,
            }}
          >
            ${value.toLocaleString()}
          </Typography>
        </Box>

        {/* Slider */}
        <DownPaymentSlider
          value={value}
          onChange={handleChange}
          min={min}
          max={max}
          step={step}
          aria-label='Down Payment'
        />

        {/* Min and Max labels */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            // mt: 1,
          }}
        >
          <Typography
            variant='caption'
            sx={{
              color: '#999',
              fontSize: '14px',
            }}
          >
            {formatCurrency(min)}
          </Typography>
          <Typography
            variant='caption'
            sx={{
              color: '#999',
              fontSize: '14px',
            }}
          >
            {formatCurrency(max)}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        pb: 3,
        overflowY: 'auto',
        borderRadius: { xs: 2, sm: 3 },
        flex: 1,
        background: BACKGROUND_GRADIENT,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3,
          py: 1,
          mb: 2,
          textAlign: 'center',
          background:
            'linear-gradient(to right, #B1CAFF, #ffffff, #ffffff, #ffffff)',
          borderBottom: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.light, 0.1),
        }}
      >
        <IconButton
          onClick={() => onBack()}
          sx={{ mr: 0.5, p: 0.5, pl: 0 }}
          aria-label='Go back'
        >
          <KeyboardBackspaceIcon sx={{ color: 'primary.light' }} />
        </IconButton>
        <Typography fontWeight={400} color={'primary.light'}>
          Back to Vehicle
        </Typography>
      </Box>

      <Box sx={{ px: 2 }}>
        <Typography
          variant='h6'
          fontWeight={500}
          sx={{ mb: 1, textAlign: 'center' }}
        >
          Payments
        </Typography>

        <Card
          sx={{
            backgroundColor: '#fff',
            backgroundImage: BACKGROUND_GRADIENT_TRANSPARENT,
          }}
        >
          <Box
            sx={{
              px: 2,
              pt: 2,
              pb: 1,
              gap: 0.2,
              borderBottom: '1px solid',
              borderColor: (theme) => alpha(theme.palette.primary.light, 0.1),
            }}
          >
            <Typography fontSize='14px' fontWeight={500}>
              {carData?.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontSize: '16px', // 18px
                  fontWeight: 600,
                  // background: (theme: any) => theme.palette.gradients.primary,
                  // backgroundClip: 'text',
                  // WebkitBackgroundClip: 'text',
                  // WebkitTextFillColor: 'transparent',
                  // color: 'transparent',
                }}
              >
                {parsedPrice?.amount !== '-'
                  ? `${parsedPrice?.symbolOrCode}${parsedPrice?.amount}`
                  : '-'}
              </Typography>
              <Typography
                fontSize='12px'
                fontWeight={400}
                color={'text.secondary'}
              >
                {' · '}
                {carData?.mileage ? `${carData?.mileage ?? 0} Miles` : ''}{' '}
                {' · '}
                {carData?.engine ? `${carData?.engine ?? 0}` : ''}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: (theme) => alpha(theme.palette.primary.light, 0.1),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
              }}
            >
              <Typography
                variant='h4'
                fontWeight={600}
              >{`${parsedPrice?.symbolOrCode}${formatEMIAmount(Math.ceil(calculateEMI(parseFloat(parsedPrice.amount.replace(',', '')))))}`}</Typography>
              <Typography
                variant='h6'
                fontWeight={400}
                sx={{
                  '& span': {
                    color: 'text.secondary',
                  },
                }}
              >
                &nbsp;/ mo <span>(est.)</span>
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: typographyClamps.caption,
                textAlign: 'center',
                color: 'text.secondary',
                mb: 1,
              }}
            >{`72 months · $${value.toLocaleString()} down · Est. APR`}</Typography>
            {getDownPaymentView({})}
          </Box>

          <Box
            sx={{
              p: 2,
            }}
          >
            <Button
              fullWidth
              variant='contained'
              onClick={() => onSubmit(value)}
              sx={{
                py: 1.5,
                color: '#ffffff',
                background: BUTTON_BG_COLOR,
                textTransform: 'none',
              }}
            >
              Get Today’s Price
            </Button>

            {IsNotNullOrEmpty(
              carData?.dealer?.dealerSettings?.dealershipWebsite,
            ) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mt: 1,
                }}
                onClick={gotoDealershipWebsite}
              >
                <Typography
                  gutterBottom
                  fontSize={'12px'}
                  sx={{ mt: 1, textAlign: 'center' }}
                >
                  View full payment details on dealer site
                </Typography>
                <ArrowForwardIcon fontSize='small' color='secondary' />
              </Box>
            )}
          </Box>
        </Card>

        <Typography fontSize={'11px'} sx={{ mt: 1, textAlign: 'center' }}>
          Estimates vary by credit, taxes, fees, and lender approval.
        </Typography>
      </Box>
    </Box>
  );
};

export default PaymentInformationModel;
